import { FF_ENABLE_ESCRIPT_ORDERS } from '@/constants/featureFlags';
import { AlertOutlet } from '@/context/escript/OnboardingLayout';
import { PreOnboardingStateProvider } from '@/context/escript/PreOnboardingContext';
import { usePortalPartnerBrand } from '@/hooks/portal-partner-brand';
import useFeatureFlags from '@/hooks/useFeatureFlags';
import useCustomer from '@/hooks/user/useCustomer';
import { ESCRIPT_REFILL_ROUTE } from '@/routes/route-paths';
import { Box, CircularProgress } from '@montugroup/design-system';
import { BrandThemeProvider } from '@montugroup/themes';
import { styled } from '@mui/material';
import React, { Suspense, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { OnboardingLayout } from './OnboardingLayout';

const HeaderContainer = styled(Box)(() => ({
  position: 'sticky',
  top: 0,
  zIndex: 10,
}));

export function OnboardingUnauth({ children }: React.PropsWithChildren) {
  const { key: partnerKey } = usePortalPartnerBrand();
  const { flags } = useFeatureFlags();
  const { data: customer } = useCustomer();
  const navigate = useNavigate();
  const isEscriptCustomer = customer?.isExternal && flags[FF_ENABLE_ESCRIPT_ORDERS];

  // redirect logged in users to the submit token page within the umeds journey.
  useLayoutEffect(() => {
    if (isEscriptCustomer) {
      navigate(ESCRIPT_REFILL_ROUTE, { replace: true, state: { addNewScript: true } });
    }
  }, [flags, isEscriptCustomer, navigate]);

  return (
    <BrandThemeProvider theme={partnerKey}>
      <OnboardingLayout>
        <PreOnboardingStateProvider>
          <OnboardingLayout.Background>
            <HeaderContainer>
              <OnboardingLayout.OnboardingBanner />
              <OnboardingLayout.OnboardingHeader />
            </HeaderContainer>

            <OnboardingLayout.Alert>
              <AlertOutlet />
            </OnboardingLayout.Alert>

            <OnboardingLayout.Content>
              <Suspense fallback={<CircularProgress />}>{children}</Suspense>
            </OnboardingLayout.Content>

            <OnboardingLayout.OnboardingFooter />
          </OnboardingLayout.Background>
        </PreOnboardingStateProvider>
      </OnboardingLayout>
    </BrandThemeProvider>
  );
}
